// src/pages/T321AccessSelector.jsx
// T321 Access Type Selector — 2 cards (Individual / Ruleset). Same layout as T178AccessSelector,
// no Bindle card. Badges show the 4 T321 permission groups instead of LRP/MRP/HRP/CRP.

import React from 'react';
import { useNavigate } from 'react-router-dom';
import { BUCKETS } from '../data/t321PermissionCatalog';

// Short group names for the badges (BUCKETS titles carry Individual-flow routing text).
const GROUP_NAMES = {
    base: 'Base Permissions',
    arvt: 'ARVT Permissions',
    tools: 'Tools Permissions',
    tools_pml: 'Tools Permissions (PML Check)',
};
// Individual flow routing per group (each group becomes its own approval).
const INDIVIDUAL_ROUTING = {
    base: 'CTPS only',
    arvt: 'L7+ leader → CTPS',
    tools: 'CTPS only',
    tools_pml: 'PML training + CTPS',
};

// Ruleset flow routing per group (each group becomes its own approval).
const RULESET_ROUTING = {
    base: 'CTPS only',
    arvt: 'L7+ leader → CTPS',
    tools: 'CTPS only',
    tools_pml: 'SPS Learning Tech → L7+ leader → CTPS',
};

const groupBadges = (routingFor) => BUCKETS.map(b => ({
    code: b.id,
    label: `${b.icon} ${GROUP_NAMES[b.id] || b.title}: ${routingFor(b.id)}`,
    color: b.color,
    bg: b.bgColor,
    border: b.borderColor,
}));

const T321AccessSelector = () => {
    const navigate = useNavigate();

    const accessOptions = [
        {
            id: 'individual',
            icon: '🔑',
            title: 'Individual Permissions Access',
            description: 'Request specific T321 permissions for named users. Each permission group becomes its own approval.',
            badges: groupBadges(id => INDIVIDUAL_ROUTING[id]),
            route: '/t321/individual'
        },
        {
            id: 'ruleset',
            icon: '📋',
            title: 'Ruleset Access',
            description: 'Request rule-based access (for example, all reports of a manager, or a job family/code) instead of listing users one by one. All four T321 permission groups are eligible, and each group becomes its own approval.',
            badges: groupBadges(id => RULESET_ROUTING[id]),
            note: 'ℹ️ Base and Tools rulesets are reviewed by the CTPS Permissions team only. ARVT needs an L7+ leader, then CTPS. Tools (PML Check) goes to SPS Learning Tech (manual PML check), then an L7+ leader, then CTPS. A business justification is required.',
            route: '/t321/ruleset'
        }
    ];

    const go = (option) => navigate(option.route);

    return (
        <div style={{ maxWidth: '1100px', margin: '0 auto', padding: '48px 24px' }}>
            {/* Title */}
            <div style={{ marginBottom: '32px' }}>
                <h1 style={{ fontSize: '26px', fontWeight: '800', color: '#111', margin: '0 0 6px' }}>🔐 T321 Permission Request</h1>
                <p style={{ fontSize: '14px', color: '#666', margin: 0 }}>Choose how you'd like to request access to Tenant 321 permissions</p>
            </div>

            {/* Access Type Cards */}
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '16px' }}>
                {accessOptions.map(option => (
                    <div
                        key={option.id}
                        role="link"
                        tabIndex={0}
                        aria-label={`${option.title} — request access`}
                        onClick={() => go(option)}
                        onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); go(option); } }}
                        style={{
                            padding: '24px 20px',
                            borderRadius: '12px',
                            border: '2px solid #22c55e',
                            background: '#fff',
                            cursor: 'pointer',
                            transition: 'all 0.2s ease',
                            display: 'flex',
                            flexDirection: 'column',
                            gap: '12px',
                            boxShadow: '0 2px 8px rgba(34,197,94,0.08)',
                            position: 'relative'
                        }}
                        onMouseEnter={(e) => { e.currentTarget.style.boxShadow = '0 6px 20px rgba(34,197,94,0.15)'; e.currentTarget.style.transform = 'translateY(-2px)'; }}
                        onMouseLeave={(e) => { e.currentTarget.style.boxShadow = '0 2px 8px rgba(34,197,94,0.08)'; e.currentTarget.style.transform = 'translateY(0)'; }}
                    >
                        {/* Badge top-right */}
                        <span style={{
                            position: 'absolute', top: '12px', right: '12px',
                            padding: '2px 10px', borderRadius: '12px', fontSize: '10px', fontWeight: '700',
                            backgroundColor: '#dcfce7', color: '#166534', border: '1px solid #bbf7d0'
                        }}>
                            Active
                        </span>

                        <div style={{ fontSize: '32px' }} aria-hidden="true">{option.icon}</div>

                        <h3 style={{ fontSize: '16px', fontWeight: '700', color: '#111', margin: 0 }}>
                            {option.title}
                        </h3>

                        <p style={{ fontSize: '13px', color: '#555', margin: 0, lineHeight: '1.5' }}>
                            {option.description}
                        </p>

                        {/* Permission group badges */}
                        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px' }}>
                            {option.badges.map(b => (
                                <span key={b.code} style={{
                                    padding: '3px 10px', borderRadius: '14px', fontSize: '11px', fontWeight: '600',
                                    backgroundColor: b.bg, color: '#1f2937', border: `1px solid ${b.border}`
                                }}>
                                    {b.label}
                                </span>
                            ))}
                        </div>

                        {option.note && (
                            <div style={{ padding: '8px 10px', borderRadius: '6px', background: '#eff6ff', border: '1px solid #bfdbfe', fontSize: '11px', color: '#1e3a8a', lineHeight: '1.4' }}>
                                {option.note}
                            </div>
                        )}

                        <div style={{ marginTop: '4px', fontSize: '13px', fontWeight: '600', color: '#15803d' }}>
                            Request Access →
                        </div>
                    </div>
                ))}
            </div>
        </div>
    );
};

export default T321AccessSelector;
